import React, { useEffect, useState, useCallback, useRef } from "react";
import { useInfiniteAppointments } from "./api/app.js";
import { DatePicker } from "antd";
import Layout from "../core/Layout";
import MedicalHistoryForm from "./medicalHistoryForm";
import AppointmentDetail from "./appointmentDetail";
import { usePatients } from "./api/patients.js";
import Search from "./nested/Search.js";
import TimelineCalendar from "./timeline.js";
import useDebounce from "../hooks/useDebounce.js";
import { getAge } from "../utils/common.js";
import { useListMedicines } from "./api/medicine";

const { RangePicker } = DatePicker;

const SCROLL_STORAGE_KEY = "appointmentScrollPosition";

const PatAppDetail = () => {
    const [searchDetails, setSearchDetails] = useState({
        firstName: "",
        lastName: "",
        phoneNo: "",
        patientNumber: "",
        aadharCard: "",
    });

    const [query, setQuery] = useState("");

    const debouncedQuery = useDebounce(query, 5000);

    const [selectedPatient, setSelectedPatient] = useState(() => {
        const savedPatient = localStorage.getItem("selectedPatient");
        return savedPatient ? JSON.parse(savedPatient) : null;
    });
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    const [content, setContent] = useState("Today");
    const [page, setPage] = useState(1);
    const [date, setDate] = useState(null);
    const scrollRef = useRef(null);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        error,
        refetch,
    } = useInfiniteAppointments({
        limit: 30,
        patient: selectedPatient?._id,
        date: date,
    });

    const {
        data: patientsData,
        isLoading: LoadPatient,
        isError,
    } = usePatients(page, searchDetails);

    const { data: medicines = [] } = useListMedicines();

    useEffect(() => {
        if (selectedPatient) {
            localStorage.setItem(
                "selectedPatient",
                JSON.stringify(selectedPatient)
            );
        }
        if (date) {
            localStorage.setItem("selectedDate", date);
        }
    }, [selectedPatient, date]);

    const handleItemClick = (patient) => {
        setSelectedPatient(patient);
    };

    const saveScrollPosition = () => {
        if (scrollRef.current) {
            localStorage.setItem(
                SCROLL_STORAGE_KEY,
                scrollRef.current.scrollTop
            );
        }
    };

    const restoreScrollPosition = () => {
        const savedScrollTop = localStorage.getItem(SCROLL_STORAGE_KEY);
        if (scrollRef.current && savedScrollTop) {
            scrollRef.current.scrollTop = parseInt(savedScrollTop, 10);
        }
    };

    useEffect(() => {
        restoreScrollPosition();
    }, []);

    useEffect(() => {
        const handleBeforeUnload = () => saveScrollPosition();
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            saveScrollPosition();
        };
    }, []);

    useEffect(() => {
        if (selectedPatient || date) {
            refetch();
        }
    }, [selectedPatient, date]);

    const handleObserver = useRef();

    const lastElement = useCallback(
        (element) => {
            if (isLoading || isFetchingNextPage) return;
            if (handleObserver.current) handleObserver.current.disconnect();

            handleObserver.current = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && hasNextPage) {
                        fetchNextPage();
                    }
                });
            });

            if (element) handleObserver.current.observe(element);
        },
        [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
    );

    if (isLoading) return <p>Loading...</p>;
    if (error) return <p>Error: {error.message}</p>;

    // FIXED: Helper function to format appointment time
    const formatAppointmentTime = (appointment) => {
        // First priority: Check for 'time' field
        if (appointment.time) {
            try {
                // If time is in HH:MM format like "09:30"
                if (
                    typeof appointment.time === "string" &&
                    appointment.time.includes(":")
                ) {
                    const [hours, minutes] = appointment.time.split(":");
                    const date = new Date();
                    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                    return date.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                    });
                }

                // If time is a Date object
                if (appointment.time instanceof Date) {
                    return appointment.time.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                    });
                }
            } catch (error) {
                console.error("Error formatting time field:", error);
            }
        }

        // Second priority: Check for separate appointmentTime field
        if (appointment.appointmentTime) {
            try {
                if (
                    typeof appointment.appointmentTime === "string" &&
                    appointment.appointmentTime.includes(":")
                ) {
                    const [hours, minutes] =
                        appointment.appointmentTime.split(":");
                    const date = new Date();
                    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                    return date.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                    });
                }
            } catch (error) {
                console.error("Error formatting appointmentTime field:", error);
            }
        }

        // Third priority: Check for scheduledTime or other time fields
        const timeFields = ["scheduledTime", "startTime", "bookingTime"];
        for (const field of timeFields) {
            if (appointment[field]) {
                try {
                    const date = new Date(appointment[field]);
                    if (!isNaN(date.getTime())) {
                        return date.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                        });
                    }
                } catch (error) {
                    console.error(`Error formatting ${field}:`, error);
                }
            }
        }

        return "Time not set";
    };

    // FIXED: Helper function to format full date and time
    const formatFullDateTime = (appointment) => {
        // Get the appointment time using the fixed function
        const appointmentTime = formatAppointmentTime(appointment);

        // Get the appointment date
        let appointmentDate = "Date not set";

        // First check for 'date' field
        if (appointment.date) {
            try {
                const date = new Date(appointment.date);
                if (!isNaN(date.getTime())) {
                    appointmentDate = date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                    });
                }
            } catch (error) {
                console.error("Error formatting date field:", error);
            }
        }
        // Then check for appointmentDate field
        else if (appointment.appointmentDate) {
            try {
                const date = new Date(appointment.appointmentDate);
                if (!isNaN(date.getTime())) {
                    appointmentDate = date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                    });
                }
            } catch (error) {
                console.error("Error formatting appointmentDate field:", error);
            }
        }

        return {
            time: appointmentTime,
            date: appointmentDate,
        };
    };

    return (
        <Layout>
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    paddingLeft: "280px",
                }}
            >
                <div className="mb-4">
                    <div>
                        {selectedPatient && (
                            <div
                                style={{
                                    background:
                                        "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)",
                                    color: "white",
                                    padding: "20px",
                                    borderRadius: "12px",
                                    marginBottom: "20px",
                                    boxShadow:
                                        "0 8px 32px rgba(8, 145, 178, 0.2)",
                                }}
                            >
                                <h2
                                    style={{
                                        margin: 0,
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {selectedPatient.firstName.toUpperCase()}{" "}
                                    {selectedPatient.lastName.toUpperCase()}{" "}
                                    {selectedPatient.age
                                        ? `${selectedPatient.age} years`
                                        : getAge(selectedPatient.birthDate) !==
                                          0
                                        ? `${getAge(
                                              selectedPatient.birthDate
                                          )} years`
                                        : ""}
                                </h2>
                                <p
                                    style={{
                                        margin: "5px 0 0 0",
                                        opacity: 0.9,
                                    }}
                                >
                                    {selectedPatient.phoneNo
                                        ? `Contact Number : ${selectedPatient.phoneNo}`
                                        : ""}
                                </p>
                                <p
                                    style={{
                                        margin: "5px 0 0 0",
                                        opacity: 0.9,
                                    }}
                                >
                                    {selectedPatient.gender
                                        ? `Gender : ${selectedPatient.gender}`
                                        : ""}
                                </p>
                                <p
                                    style={{
                                        margin: "5px 0 0 0",
                                        opacity: 0.9,
                                    }}
                                >
                                    Patient ID: {selectedPatient.patientNumber}
                                </p>
                            </div>
                        )}
                    </div>
                    <div
                        ref={scrollRef}
                        style={{ overflowY: "auto" }}
                        onScroll={saveScrollPosition}
                    >
                        {data?.pages?.map((group, index) =>
                            group.appointment.map((app, i) => {
                                const isLastItem =
                                    index === data.pages.length - 1 &&
                                    i === group.appointment.length - 1;
                                return (
                                    <AppointmentDetail
                                        lastElement={
                                            isLastItem ? lastElement : null
                                        }
                                        key={app._id}
                                        app={app}
                                        medicines={medicines}
                                    />
                                );
                            })
                        )}
                        {isFetchingNextPage && <p>Loading more...</p>}
                    </div>
                    {date && (
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => setDate(null)}
                            style={{
                                background:
                                    "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                                border: "none",
                                borderRadius: "8px",
                                padding: "10px 20px",
                                fontWeight: "600",
                                boxShadow: "0 4px 15px rgba(5, 150, 105, 0.3)",
                            }}
                        >
                            Show More
                        </button>
                    )}
                </div>
                {selectedPatient && (
                    <MedicalHistoryForm id={selectedPatient._id} />
                )}
            </div>

            {/* Enhanced Sidebar with Hospital-appropriate colors */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 60,
                    width: "280px",
                    height: "100vh",
                    background:
                        "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)",
                    borderRight: "1px solid #bae6fd",
                    boxShadow: "4px 0 20px rgba(8, 145, 178, 0.1)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Header Section */}
                <div
                    style={{
                        padding: "20px",
                        background: "white",
                        borderBottom: "1px solid #bae6fd",
                        marginTop: "70px",
                    }}
                >
                    <h3
                        style={{
                            margin: "0 0 20px 0",
                            color: "#0c4a6e",
                            fontSize: "18px",
                            fontWeight: "bold",
                        }}
                    >
                        Patient Dashboard
                    </h3>

                    {/* Tab Buttons with medical colors */}
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            marginBottom: "20px",
                        }}
                    >
                        <button
                            onClick={() => setContent("Today")}
                            style={{
                                flex: 1,
                                padding: "12px 16px",
                                border: "none",
                                borderRadius: "10px",
                                fontSize: "14px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                background:
                                    content === "Today"
                                        ? "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)"
                                        : "#f1f5f9",
                                color:
                                    content === "Today" ? "white" : "#64748b",
                                boxShadow:
                                    content === "Today"
                                        ? "0 4px 15px rgba(8, 145, 178, 0.3)"
                                        : "none",
                            }}
                        >
                            📅 Today
                        </button>
                        <button
                            onClick={() => setContent("All")}
                            style={{
                                flex: 1,
                                padding: "12px 16px",
                                border: "none",
                                borderRadius: "10px",
                                fontSize: "14px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                background:
                                    content === "All"
                                        ? "linear-gradient(135deg, #059669 0%, #0d9488 100%)"
                                        : "#f1f5f9",
                                color: content === "All" ? "white" : "#64748b",
                                boxShadow:
                                    content === "All"
                                        ? "0 4px 15px rgba(5, 150, 105, 0.3)"
                                        : "none",
                            }}
                        >
                            👥 All
                        </button>
                    </div>

                    {/* Original Search Component - This is important! */}
                    <Search
                        handleItemClick={handleItemClick}
                        query={query}
                        setQuery={setQuery}
                    />
                </div>

                {/* Content Area */}
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "20px",
                        scrollbarWidth: "thin",
                    }}
                >
                    {/* Pagination Controls with medical colors */}
                    {content === "All" && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                                padding: "10px",
                                background: "white",
                                borderRadius: "10px",
                                boxShadow: "0 2px 10px rgba(8, 145, 178, 0.1)",
                                border: "1px solid #bae6fd",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    color: "#0c4a6e",
                                    fontWeight: "500",
                                }}
                            >
                                Page {page}
                            </span>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    style={{
                                        border: "none",
                                        background:
                                            page === 1
                                                ? "#f1f5f9"
                                                : "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)",
                                        color: page === 1 ? "#94a3b8" : "white",
                                        padding: "8px 12px",
                                        borderRadius: "6px",
                                        cursor:
                                            page === 1
                                                ? "not-allowed"
                                                : "pointer",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                    }}
                                    disabled={page === 1}
                                    onClick={() => setPage((p) => p - 1)}
                                >
                                    ← Prev
                                </button>
                                <button
                                    style={{
                                        border: "none",
                                        background:
                                            page === patientsData?.totalPages
                                                ? "#f1f5f9"
                                                : "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)",
                                        color:
                                            page === patientsData?.totalPages
                                                ? "#94a3b8"
                                                : "white",
                                        padding: "8px 12px",
                                        borderRadius: "6px",
                                        cursor:
                                            page === patientsData?.totalPages
                                                ? "not-allowed"
                                                : "pointer",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                    }}
                                    disabled={page === patientsData?.totalPages}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Today's Appointments with FIXED date/time formatting */}
                    {content === "Today" &&
                        data?.pages?.[0]?.todayAppointment && (
                            <div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginBottom: "15px",
                                    }}
                                >
                                    <h4
                                        style={{
                                            margin: 0,
                                            color: "#0c4a6e",
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Today's Appointments
                                    </h4>
                                    <span
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)",
                                            color: "white",
                                            padding: "4px 12px",
                                            borderRadius: "20px",
                                            fontSize: "12px",
                                            fontWeight: "600",
                                        }}
                                    >
                                        {data.pages[0].todayAppointment.length}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "12px",
                                    }}
                                >
                                    {data.pages[0].todayAppointment.map(
                                        (item, index) => {
                                            // Use the FIXED formatting function
                                            const dateTime =
                                                formatFullDateTime(item);

                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        background: "white",
                                                        padding: "16px",
                                                        borderRadius: "12px",
                                                        cursor: "pointer",
                                                        border: "1px solid #bae6fd",
                                                        boxShadow:
                                                            "0 2px 10px rgba(8, 145, 178, 0.1)",
                                                        transition:
                                                            "all 0.3s ease",
                                                    }}
                                                    onClick={() => {
                                                        refetch();
                                                        setDate(
                                                            new Date().toDateString()
                                                        );
                                                        setSelectedPatient(
                                                            item.patient || item
                                                        );
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.transform =
                                                            "translateY(-2px)";
                                                        e.currentTarget.style.boxShadow =
                                                            "0 8px 25px rgba(8, 145, 178, 0.2)";
                                                        e.currentTarget.style.borderColor =
                                                            "#0891b2";
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.transform =
                                                            "translateY(0)";
                                                        e.currentTarget.style.boxShadow =
                                                            "0 2px 10px rgba(8, 145, 178, 0.1)";
                                                        e.currentTarget.style.borderColor =
                                                            "#bae6fd";
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            marginBottom: "8px",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: "40px",
                                                                height: "40px",
                                                                borderRadius:
                                                                    "50%",
                                                                background:
                                                                    "linear-gradient(135deg, #0891b2 0%, #0284c7 100%)",
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                marginRight:
                                                                    "12px",
                                                            }}
                                                        >
                                                            <i
                                                                className="fas fa-user-md"
                                                                style={{
                                                                    color: "white",
                                                                    fontSize:
                                                                        "16px",
                                                                }}
                                                            ></i>
                                                        </div>
                                                        <div
                                                            style={{ flex: 1 }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    color: "#0c4a6e",
                                                                    fontSize:
                                                                        "14px",
                                                                    marginBottom:
                                                                        "4px",
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    flexWrap:
                                                                        "wrap",
                                                                    gap: "6px",
                                                                }}
                                                            >
                                                                <span>
                                                                    {item
                                                                        .patient
                                                                        ?.firstName ||
                                                                        item.firstName}{" "}
                                                                    {item
                                                                        .patient
                                                                        ?.lastName ||
                                                                        item.lastName}
                                                                </span>

                                                                <div
                                                                    style={{
                                                                        display:
                                                                            "flex",
                                                                        gap: "4px",
                                                                        flexWrap:
                                                                            "wrap",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontSize:
                                                                                "10px",
                                                                            color: "#059669",
                                                                            fontWeight:
                                                                                "600",
                                                                            background:
                                                                                "#f0fdf4",
                                                                            padding:
                                                                                "2px 6px",
                                                                            borderRadius:
                                                                                "8px",
                                                                            border: "1px solid #bbf7d0",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                        }}
                                                                    >
                                                                        🕐{" "}
                                                                        {
                                                                            dateTime.time
                                                                        }
                                                                    </span>
                                                                    <span
                                                                        style={{
                                                                            fontSize:
                                                                                "10px",
                                                                            color: "#0891b2",
                                                                            fontWeight:
                                                                                "600",
                                                                            background:
                                                                                "#f0f9ff",
                                                                            padding:
                                                                                "2px 6px",
                                                                            borderRadius:
                                                                                "8px",
                                                                            border: "1px solid #bae6fd",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                        }}
                                                                    >
                                                                        📅{" "}
                                                                        {
                                                                            dateTime.date
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <span
                                                                    style={{
                                                                        color: "green",
                                                                    }}
                                                                >
                                                                    Dr.
                                                                    {
                                                                        item
                                                                            ?.doctor
                                                                            ?.name
                                                                    }
                                                                </span>
                                                            </div>

                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        "12px",
                                                                    color: "#64748b",
                                                                }}
                                                            >
                                                                ID:{" "}
                                                                {item.patient
                                                                    ?.patientNumber ||
                                                                    item.patientNumber}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        )}

                    {/* All Patients with medical colors */}
                    {content === "All" && patientsData?.patient && (
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "15px",
                                }}
                            >
                                <h4
                                    style={{
                                        margin: 0,
                                        color: "#0c4a6e",
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    All Patients
                                </h4>
                                <span
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                                        color: "white",
                                        padding: "4px 12px",
                                        borderRadius: "20px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                    }}
                                >
                                    {patientsData.patient.length}
                                </span>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                }}
                            >
                                {patientsData.patient.map((pat, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            background: "white",
                                            padding: "14px",
                                            borderRadius: "10px",
                                            cursor: "pointer",
                                            border: "1px solid #bae6fd",
                                            boxShadow:
                                                "0 2px 8px rgba(8, 145, 178, 0.1)",
                                            transition: "all 0.3s ease",
                                        }}
                                        onClick={() => {
                                            setDate(null);
                                            setSelectedPatient(pat);
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.transform =
                                                "translateY(-1px)";
                                            e.currentTarget.style.boxShadow =
                                                "0 4px 15px rgba(5, 150, 105, 0.2)";
                                            e.currentTarget.style.borderColor =
                                                "#059669";
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.transform =
                                                "translateY(0)";
                                            e.currentTarget.style.boxShadow =
                                                "0 2px 8px rgba(8, 145, 178, 0.1)";
                                            e.currentTarget.style.borderColor =
                                                "#bae6fd";
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "35px",
                                                    height: "35px",
                                                    borderRadius: "50%",
                                                    background:
                                                        "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginRight: "12px",
                                                }}
                                            >
                                                <i
                                                    className="fas fa-user-md"
                                                    style={{
                                                        color: "white",
                                                        fontSize: "14px",
                                                    }}
                                                ></i>
                                            </div>
                                            <div>
                                                <div
                                                    style={{
                                                        fontWeight: "600",
                                                        color: "#0c4a6e",
                                                        fontSize: "14px",
                                                        marginBottom: "4px",
                                                    }}
                                                >
                                                    {pat?.firstName}-
                                                    {pat?.patientNumber}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "#64748b",
                                                    }}
                                                >
                                                    ID: {pat?.patientNumber}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default PatAppDetail;
